import { createAction, createAsyncThunk } from '@reduxjs/toolkit';

import { handleError } from '../../../utility/helpers';
import { downloadFile, request, requestWithParam, upload } from '../../../utility/apiClients';
import { REDUCER_ENUMS } from '../../../enums/applicationEnums';
import { APIS_ENDPOINTS, APIS_METHOD } from '../../../resources/api-constants';

export const uploadDocuments = createAsyncThunk(
    REDUCER_ENUMS.AI_UPLOAD_DOC,
    async (payload: any, { rejectWithValue }) => {
        try {
            const response = await request(APIS_METHOD.POST, APIS_ENDPOINTS.AI_UPLOAD_DOC, payload, false);
            return response.data;
        } catch (err) {
            return handleError(err, rejectWithValue);
        }
    },
);

export type SpeechToTextPayload = {
    audio: Blob;
    filename?: string;
    language?: string;
    prompt?: string;
};

export const speechToText = createAsyncThunk(
    REDUCER_ENUMS.AI_SPEECH_TO_TEXT,
    async ({ audio, filename, language, prompt }: SpeechToTextPayload, { rejectWithValue }) => {
        try {
            const formData = new FormData();
            const resolvedName =
                filename ||
                (audio.type.includes('webm')
                    ? 'recording.webm'
                    : audio.type.includes('mp4') || audio.type.includes('m4a')
                        ? 'recording.m4a'
                        : audio.type.includes('ogg')
                            ? 'recording.ogg'
                            : audio.type.includes('wav')
                                ? 'recording.wav'
                                : 'recording.audio');
            formData.append('audio', audio, resolvedName);
            if (language) formData.append('language', language);
            if (prompt) formData.append('prompt', prompt);

            const response = await upload(APIS_ENDPOINTS.AI_SPEECH_TO_TEXT, formData);
            return response.data;
        } catch (err) {
            return handleError(err, rejectWithValue);
        }
    },
);


export const getChats = createAsyncThunk(
    'employer-ai-chat',
    async (queryParams: any, { rejectWithValue }) => {
        try {
            // Determine the endpoint based on the step parameter
            // The endpoint URL itself identifies which page we're on
            let endpoint = APIS_ENDPOINTS.AI_UPLOAD_DOC;
            if (queryParams.step === 'job-description') {
                endpoint = APIS_ENDPOINTS.JOB_DESCRIPTION_CHAT;
            } else if (queryParams.step === 'idealize-candidate') {
                endpoint = APIS_ENDPOINTS.IDEALIZED_CANDIDATE_CHAT;
            }

            // Remove the step parameter from queryParams before sending to API
            // The endpoint URL already identifies the page
            const { step, ...apiQueryParams } = queryParams;

            const response = await requestWithParam(
                APIS_METHOD.GET,
                endpoint,
                {},
                apiQueryParams,
                false
            );
            if (response) {
                const { results, count } = response.data;
                return { responseStatus: true, data: results, total: count, queryParams };
            } else {
                return { responseStatus: false, data: [], total: 0, queryParams, studentCount: 0 };
            }
        } catch (err) {
            return handleError(err, rejectWithValue);
        }
    },
);

export const generateJob = createAsyncThunk(
    'generate-job',
    async (payload: any, { rejectWithValue }) => {
        try {
            const response = await request(APIS_METHOD.POST, '/job', payload, false);
            return response.data;
        } catch (err) {
            return handleError(err, rejectWithValue);
        }
    },
);

export const getJobDetail = createAsyncThunk(
    'job-detail',
    async (id: any, { rejectWithValue }) => {
        try {
            const response = await request(
                APIS_METHOD.GET,
                `/job/${id}/`,
                {},
                false,
            );
            return response.data;
        } catch (err) {
            return handleError(err, rejectWithValue);
        }
    },
);

export const getJobNPDetail = createAsyncThunk(
    'job-NP-detail',
    async (id: any, { rejectWithValue }) => {
        try {
            const response = await request(
                APIS_METHOD.GET,
                `/job-neer-peer/${id}/`,
                {},
                true,
            );
            return response.data;
        } catch (err) {
            return handleError(err, rejectWithValue);
        }
    },
);

export const updateJob = createAsyncThunk(
    'update-job',
    async ({ payload, id }: any, { rejectWithValue }) => {
        try {
            const response = await request(
                APIS_METHOD.PATCH,
                `/job/${id}`,
                payload,
                false
            );
            return response.data;
        } catch (err) {
            return handleError(err, rejectWithValue);
        }
    },
);

export const jobAIChat = createAsyncThunk(
    'job-ai-chat',
    async ({ payload, id, step }: any, { rejectWithValue }) => {
        try {
            // Determine the endpoint based on the step parameter
            let endpoint = `/job/${id}/ai_chat`;
            if (step === 'job-description') {
                endpoint = APIS_ENDPOINTS.JOB_DESCRIPTION_CHAT;
            } else if (step === 'idealize-candidate') {
                endpoint = APIS_ENDPOINTS.IDEALIZED_CANDIDATE_CHAT;
            }

            const response = await request(
                APIS_METHOD.POST,
                endpoint,
                payload,
                false
            );
            return response.data;
        } catch (err) {
            return handleError(err, rejectWithValue);
        }
    },
);

export const approvedJob = createAsyncThunk(
    'approve-job',
    async ({ payload, id }: any, { rejectWithValue }) => {
        try {
            const response = await request(
                APIS_METHOD.POST,
                `/job/${id}/approve_job/`,
                payload,
                false
            );
            return response.data;
        } catch (err) {
            return handleError(err, rejectWithValue);
        }
    },
);

export const resetSearchFilters = createAsyncThunk(
    'reset-search-filters',
    async (payload: { job: string }, { rejectWithValue }) => {
        try {
            const response = await request(
                APIS_METHOD.POST,
                '/job-candidate/reset-search-filters/',
                payload,
                false,
            );
            return response.data;
        } catch (err) {
            return handleError(err, rejectWithValue);
        }
    },
);

export const candidateSelectionGraphData = createAsyncThunk(
    'generate-graph',
    async (payload: any, { rejectWithValue }) => {
        try {
            const response = await request(
                APIS_METHOD.POST,
                '/job-candidate/graph-generate/',
                payload,
                false
            );
            return response.data;
        } catch (err) {
            return handleError(err, rejectWithValue);
        }
    },
);

export const candidateSelectionMilesGraphData = createAsyncThunk(
    'generate-per-miles-graph',
    async (payload: any, { rejectWithValue }) => {
        try {
            const response = await request(
                APIS_METHOD.POST,
                '/job-candidate/candidate-distance-matching/',
                payload,
                false
            );
            return response.data;
        } catch (err) {
            return handleError(err, rejectWithValue);
        }
    },
);

export const getCandidatesList = createAsyncThunk(
    'get-candidates-list',
    async (queryParams: any, { rejectWithValue }) => {
        try {
            const response = await requestWithParam(
                APIS_METHOD.GET,
                '/job-candidate/candidate/',
                {},
                queryParams,
                false
            );
            if (response) {
                const { results, count, total_count, filter_count, candidate_created, next, tier_limitation } = response.data;
                return { data: results, itemsPerPage: count, queryParams, totalItems: total_count, filterCount: filter_count, generatedDate: candidate_created, nextPage: next, tier_limitation };
            } else {
                return { data: [], total: 0, queryParams, filterCount: 0 };
            }
        } catch (err) {
            return handleError(err, rejectWithValue);
        }
    }
);

export const generateCandidate = createAsyncThunk(
    'generate-candidate',
    async (payload: any, { rejectWithValue }) => {
        try {
            const response = await request(
                APIS_METHOD.POST,
                '/job-candidate/generate-candidate/',
                payload,
                false
            );
            return response.data;
        } catch (err) {
            return handleError(err, rejectWithValue);
        }
    },
);

export const getCandidateProfile = createAsyncThunk(
    'candidate-profile-detail',
    async ({ id, job_id }: any, { rejectWithValue }) => {
        try {
            const response = await request(
                APIS_METHOD.GET,
                `/job-candidate/candidate/${id}/?job_id=${job_id}`,
                {},
                false,
            );
            return response.data;
        } catch (err) {
            return handleError(err, rejectWithValue);
        }
    },
);

export const getCareerTrajectoryAnalysis = createAsyncThunk(
    'candidate-career-trajectory-analysis',
    async ({ id, job_id, regenerate }: any, { rejectWithValue }) => {
        try {
            const queryParams = new URLSearchParams();
            if (job_id) queryParams.set('job_id', String(job_id));
            if (regenerate) queryParams.set('regenerate', 'true');
            const query = queryParams.toString() ? `?${queryParams.toString()}` : '';
            const response = await request(
                APIS_METHOD.GET,
                `/job-candidate/candidate/${id}/career-trajectory-analysis/${query}`,
                {},
                false,
            );
            return response.data;
        } catch (err) {
            return handleError(err, rejectWithValue);
        }
    },
);

export const getJobsList = createAsyncThunk(
    'get-jobs-list',
    async (queryParams: any, { rejectWithValue }) => {
        try {
            const response = await requestWithParam(
                APIS_METHOD.GET,
                '/job/',
                {},
                queryParams,
                false
            );
            if (response) {
                const { results, count, total_count, candidate_created, next } = response.data;
                return { data: results, itemsPerPage: count, queryParams, totalItems: total_count, generatedDate: candidate_created, nextPage: next };
            } else {
                return { data: [], total: 0, queryParams };
            }
        } catch (err) {
            return handleError(err, rejectWithValue);
        }
    }
);

export const deleteJob = createAsyncThunk(
    'delete-job',
    async (jobId: any, { rejectWithValue }) => {
        try {
            const response = await request(
                APIS_METHOD.DELETE,
                `/job/${jobId}`,
                {},
                false
            );
            return response.data;
        } catch (err) {
            return handleError(err, rejectWithValue);
        }
    },
);

export const duplicateJobData = createAsyncThunk(
    'job-duplicate',
    async (payload: any, { rejectWithValue }) => {
        try {
            const response = await request(
                APIS_METHOD.POST,
                `/duplicate-job/`,
                payload,
                false,
            );
            return response.data;
        } catch (err) {
            return handleError(err, rejectWithValue);
        }
    },
);

export const exportCandidates = createAsyncThunk(
    'export-candidates-list',
    async (queryParam: any, { rejectWithValue }) => {
        try {
            const queryString = new URLSearchParams(queryParam).toString();
            const url = `/job-candidate/candidate/?${queryString}`;
            return await downloadFile(url);
        } catch (err) {
            return handleError(err, rejectWithValue);
        }
    },
);

export const aiChat = createAsyncThunk(
    'ai-chat',
    async (payload: any, { rejectWithValue }) => {
        try {
            const response = await request(APIS_METHOD.POST, '/ai/auto-chat', payload, false);
            return response.data;
        } catch (err) {
            return handleError(err, rejectWithValue);
        }
    },
);

export const moveICPSuggestion = createAsyncThunk(
    'icp-move-suggestion',
    async (payload: { conversation_id: string; suggestion: string; target_section: 'must_have' | 'should_have' | 'good_to_have' }, { rejectWithValue }) => {
        try {
            const response = await request(
                APIS_METHOD.POST,
                APIS_ENDPOINTS.IDEALIZED_CANDIDATE_MOVE_SUGGESTION,
                payload,
                false,
            );
            return response.data;
        } catch (err) {
            return handleError(err, rejectWithValue);
        }
    },
);

export const getCertificates = createAsyncThunk(
    'get-certificates',
    async (reObj: any, { rejectWithValue }) => {
        try {
            const response = await request(
                APIS_METHOD.POST,
                '/ai/candidate-certificate',
                reObj,
                false,
            );
            return response.data;
        } catch (err) {
            return handleError(err, rejectWithValue);
        }
    },
);

export const getIndustries = createAsyncThunk(
    'get-industries',
    async (searchVal: any, { rejectWithValue }) => {
        try {
            const response = await request(
                APIS_METHOD.GET,
                `/industries?name=${searchVal}`,
                {},
                false,
            );
            return response;
        } catch (err) {
            return handleError(err, rejectWithValue);
        }
    },
);

export const addJobFieldSuggestion = createAsyncThunk(
    'add-job-field-suggestion',
    async (payload: { job_id: number; field_name: string; suggestions: string[]; action: string }, { rejectWithValue }) => {
        try {
            const response = await request(
                APIS_METHOD.POST,
                APIS_ENDPOINTS.JOB_FIELD_SUGGESTION,
                payload,
                false
            );
            return response.data;
        } catch (err) {
            return handleError(err, rejectWithValue);
        }
    },
);

export const resetCandidates = createAction<string | undefined>('reset-candidates');
